'use strict';

const cacheConfig = require('public/js/lib/cache-config');
const { getItem, removeItem, setItem } = require('public/js/lib/local-storage');
const STORAGE_KEYS = require('public/js/lib/storage-keys');
const { getSelectedSupplier } = require('public/js/lib/supplier-state');

const SHIPPING_QUOTE_CLEARED_EVENT = 'shippingQuote:cleared';

/**
 * @typedef {object} CachedShippingQuote
 * @property {string} postalCode
 * @property {string} productSignature
 * @property {object} response
 * @property {string} supplierHash
 */

/**
 * Reads the stored shipping quote, or null when it is missing or expired.
 *
 * @returns {CachedShippingQuote|null}
 */
function getCachedShippingQuote() {
	const cached = getItem(STORAGE_KEYS.SHIPPING_QUOTE);

	return cached !== null && typeof cached === 'object' ? cached : null;
}

/**
 * Stores the rated quote for this product list.
 *
 * @param {object} response
 * @param {string} [supplierHash] Supplier selected when the quote was requested. Defaults to the supplier selected now.
 */
function saveCachedShippingQuote(response, supplierHash = getSelectedSupplier()?.hash ?? '') {
	const cachedQuote = {
		postalCode: readRatedPostalCode(response),
		productSignature: readProductSignature(),
		response,
		supplierHash,
	};

	setItem(STORAGE_KEYS.SHIPPING_QUOTE, cachedQuote, {
		ttl: cacheConfig.getTtlFor('shippingQuote'),
	});
}

/**
 * Removes the stored shipping quote.
 */
function clearCachedShippingQuote() {
	removeItem(STORAGE_KEYS.SHIPPING_QUOTE);
}

/**
 * True when the stored quote still matches this product list.
 * A missing location cache or supplier cache is not a change.
 *
 * @param {CachedShippingQuote|null} cached
 * @returns {boolean}
 */
function isCachedShippingQuoteCurrent(cached) {
	const response = cached?.response;
	if (response === null || typeof response !== 'object') {
		return false;
	}

	const locationPostalCode = readPresentLocationPostalCode();
	const selectedSupplierHash = getSelectedSupplier()?.hash ?? '';
	const sameLocation = locationPostalCode === null || locationPostalCode === cached.postalCode;
	const sameSupplier = selectedSupplierHash === '' || selectedSupplierHash === cached.supplierHash;

	return cached.productSignature === readProductSignature()
		&& sameLocation
		&& sameSupplier;
}

/**
 * Postal code on the rated quote, or an empty string when the response has none.
 *
 * @param {object} response
 * @returns {string}
 */
function readRatedPostalCode(response) {
	const postalCode = response?.address?.postal_code;
	if (postalCode === null || postalCode === undefined) {
		return '';
	}

	return String(postalCode).trim();
}

/**
 * Postal code from the location cache, or null when that cache is missing.
 *
 * @returns {string|null}
 */
function readPresentLocationPostalCode() {
	const cachedLocation = getItem(cacheConfig.LOCATION_CACHE_KEY);
	if (cachedLocation === null || typeof cachedLocation !== 'object') {
		return null;
	}

	const postalCode = String(cachedLocation.postalCode ?? '').trim();

	return postalCode === '' ? null : postalCode;
}

/**
 * Product lines stored with the quote.
 *
 * @returns {string}
 */
function readProductSignature() {
	const lines = new Set();
	// Omit quantity so a quantity change keeps the quote. Desktop and mobile inputs for one line count once.
	for (const input of document.querySelectorAll('.update-cart-quantity')) {
		lines.add(`${input.dataset.id ?? ''}|${input.dataset.catalog ?? ''}|${input.dataset.skuStripped ?? ''}`);
	}

	return Array.from(lines)
		.sort((left, right) => left.localeCompare(right))
		.join('\n');
}

module.exports = {
	SHIPPING_QUOTE_CLEARED_EVENT,
	clearCachedShippingQuote,
	getCachedShippingQuote,
	isCachedShippingQuoteCurrent,
	saveCachedShippingQuote,
};
